// getCanSubmitJoinForm.js
'use strict';
import {
  FEATURE_ACCOUNT_REFERRAL_CODE,
  KYC_FRONT_ONLY,
  KYC_USER_FRONT_ONLY,
} from '../RemoteConfigKeys.js';

import { createSelector } from '../resource/createCachedSelector.js';
import createCachedSelector from '../resource/createCachedSelector.js';

import getRegisterData from '../selector/getRegisterData.js';
import getOperationData from '../selector/getOperationData.js';
import getMeData from '../selector/getMeData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import {
  Progress as JoinProgress,
  JoinField,
} from '../resource/joinConstants.js';
import { Purpose as AssetPurpose } from '../resource/assetConstants.js';
import { Status as UploadStatus } from '../resource/uploadConstants.js';
import { PasswordLoginEntry } from '../resource/authEntryType.js';
import { KYC_COMPLETED } from '../serviceWorker/PusherEvents.js';

const defaultObject = {};

/**
 * Select can submit join form
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {JoinProgress.Map} joinProgress - join progress.
 * @return {boolean|undefined} Return ture if form can submit.
 */
const getCanSubmitJoinForm = createCachedSelector(
  state => getNetworkingData(state, ['submit', 'me', 'kyc'], 'isFetching'),
  state => getNetworkingData(state, ['submit', 'me', 'creator'], 'isFetching'),
  state =>
    getNetworkingData(
      state,
      ['passwordLogin', PasswordLoginEntry.JOIN],
      'isFetching'
    ),
  state =>
    getNetworkingData(state, ['register', 'bind', 'email'], 'isFetching'),
  state => {
    const draftEmail = getRegisterData(state, 'email');
    getNetworkingData(
      state,
      ['register', 'emailOtp', draftEmail],
      'isFetching'
    );
  },
  (state, joinProgress) => joinProgress,
  state => getCanSubmitPersonalInformation(state),
  state => getCanSubmitUploadIdentification(state),
  state => getCanSubmitOcrResultForm(state),
  (
    isSubmitMeKycFetching,
    isSubmitMeCreatorFetching,
    isSubmitPasswordLoginFetching,
    isEmailBindFetching,
    isEmailBindOtpFetching,
    progress,
    canSubmitPersonalInformation,
    canSubmitUploadIdentification,
    canSubmitOcrResultForm
  ) => {
    if (
      isSubmitMeKycFetching ||
      isSubmitMeCreatorFetching ||
      isSubmitPasswordLoginFetching ||
      isEmailBindFetching ||
      isEmailBindOtpFetching
    ) {
      return false;
    }
    if (JoinProgress.PERSONAL_INFORMATION === progress) {
      return canSubmitPersonalInformation;
    }
    if (JoinProgress.UPLOAD_IDENTIFICATION === progress) {
      return canSubmitUploadIdentification;
    }
    if (JoinProgress.OCR_RESULT === progress) {
      return canSubmitOcrResultForm;
    }
    if (
      [JoinProgress.OCR_PROCESSING, JoinProgress.MANUAL_REVIEW].includes(
        progress
      )
    ) {
      return true;
    }
    return;
  }
)((state, joinProgress) => `${joinProgress}`);

export default getCanSubmitJoinForm;

/**
 * Select can submit upload identification form
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @return {boolean|undefined} Return ture if form can submit.
 */
export const getCanSubmitPersonalInformation = createSelector(
  state => getMeData(state, 'id'),
  state => getMeData(state, 'username'),
  state => getMeData(state, 'accountLinks'),
  state =>
    getOperationData(state, ['join', 'draft', JoinField.GENDER], 'value'),
  state =>
    getOperationData(
      state,
      ['join', 'draft', JoinField.GENDER, 'error'],
      'i18nKey'
    ),
  state =>
    getOperationData(state, ['join', 'draft', JoinField.COUNTRY], 'value'),
  state =>
    getOperationData(
      state,
      ['join', 'draft', JoinField.COUNTRY, 'error'],
      'i18nKey'
    ),
  state => getOperationData(state, ['acquisition', 'manual'], 'code'),
  state => getOperationData(state, ['acquisition'], 'errorI18nkey'),
  state => getRemoteConfigData(state, FEATURE_ACCOUNT_REFERRAL_CODE),
  state => {
    const code = getOperationData(state, ['acquisition', 'manual'], 'code');
    return getNetworkingData(
      state,
      ['acquisition', code, 'check'],
      'isFetched'
    );
  },
  state => getRegisterData(state, 'username'),
  state => getRegisterData(state, 'usernameValidation'),
  state => getRegisterData(state, 'password'),
  state => getRegisterData(state, 'passwordValidation'),
  state => getRegisterData(state, 'email'),
  state => getRegisterData(state, 'isEmailFormatCorrect'),
  state =>
    getOperationData(
      state,
      ['join', 'draft', JoinField.MEDIA_CONSENT],
      'value'
    ),
  (
    meId,
    meUsername,
    accountLink = defaultObject,
    draftGender,
    draftGenderError,
    draftCountry,
    draftCountryError,
    referralCode,
    referralCodeError,
    isFeatureReferralCodeEnabled,
    isCheckReferralCodeFetched,
    draftUsername,
    usernameValidation,
    draftPassword,
    passwordValidation,
    draftEmail,
    isEmailFormatCorrect,
    mediaConsent
  ) => {
    const boundEmail = accountLink?.email?.id;
    const isEmailValid = boundEmail
      ? true
      : !!(draftEmail && isEmailFormatCorrect);
    const isGenderValid = !!draftGender && !draftGenderError;
    const isCountryValid = !!draftCountry && !draftCountryError;
    const isUsernameValid = meUsername
      ? true
      : !!(draftUsername && usernameValidation === 'pass');
    const isPasswordValid = meId
      ? true
      : !!(draftPassword && passwordValidation === 'pass');
    const isReferralCodeValid =
      isFeatureReferralCodeEnabled && referralCode
        ? !referralCodeError && isCheckReferralCodeFetched
        : true;
    const isMediaConsentChecked = !!mediaConsent;
    return (
      isEmailValid &&
      isUsernameValid &&
      isPasswordValid &&
      isGenderValid &&
      isCountryValid &&
      isReferralCodeValid &&
      isMediaConsentChecked
    );
  }
);

/**
 * Select can submit upload identification form
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @return {boolean|undefined} Return ture if form can submit.
 */
export const getCanSubmitUploadIdentification = createSelector(
  state =>
    getOperationData(
      state,
      ['join', 'draft', AssetPurpose.KYC_FACE],
      'uploadJobId'
    ),
  state =>
    getOperationData(
      state,
      ['join', 'draft', AssetPurpose.KYC_DOCUMENT],
      'uploadJobId'
    ),
  state =>
    getOperationData(
      state,
      ['join', 'draft', AssetPurpose.KYC_DOCUMENT_BACK],
      'uploadJobId'
    ),
  state => getOperationData(state, ['uploadJob']),
  state => getIsDocumentFrontOnly(state),
  (
    faceJobId,
    documentJobId,
    documentBackJobId,
    uploadJobObject = defaultObject,
    isDocumentFrontOnly
  ) => {
    const jobIds = [faceJobId, documentJobId];
    if (!isDocumentFrontOnly) {
      jobIds.push(documentBackJobId);
    }
    return jobIds
      .map(jobId => uploadJobObject?.[jobId]?.status)
      .every(status => UploadStatus.UPLOAD_COMPLETED === status);
  }
);

/**
 * Select can submit ocr result form
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @return {boolean|undefined} Return ture if form can submit.
 */
export const getCanSubmitOcrResultForm = createSelector(
  state =>
    getOperationData(
      state,
      ['join', 'draft', JoinField.OCR_RESULT_CONFIRMED],
      'value'
    ),
  state => getOperationData(state, ['join', 'kyc'], 'kycId'),
  state => getOperationData(state, ['join', 'kyc'], 'status'),
  (isConfirmed, kycId, kycStatus) => {
    return isConfirmed && !!kycId && KYC_COMPLETED === kycStatus;
  }
);

/**
 * Get should check document front only
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {boolean} [isJoinCreator=true] - is join creator.
 * @return {boolean} Return true means should only check document front.
 */
export const getIsDocumentFrontOnly = createCachedSelector(
  state => getMeData(state, 'creator'),
  (state, isJoinCreator = true) =>
    getRemoteConfigData(
      state,
      isJoinCreator ? KYC_FRONT_ONLY : KYC_USER_FRONT_ONLY
    ),
  state =>
    getOperationData(
      state,
      ['join', 'draft', JoinField.DOCUMENT_TYPE],
      'value'
    ),
  (meCreator = defaultObject, documentFrontOnlyList, currentDocumentType) => {
    const { country: currentCountry } = meCreator;
    const isFrontOnly = !!documentFrontOnlyList.find(item => {
      const [country, documentType] = item.split(':');
      const isCountryMatched = country === currentCountry || country === '*';
      const isDocumentMatched =
        documentType === '*' || documentType === currentDocumentType;
      return isDocumentMatched && isCountryMatched;
    });
    return isFrontOnly;
  }
)((state, isJoinCreator) => `${isJoinCreator}`);
